import type { ThemeConfig, ThemeTranslations } from './types'
import { defaultTheme, defaultTranslations } from './default-theme'

/**
 * Check if value is a plain object (not DOM node, Date, etc.)
 */
function isPlainObject(value: any): boolean {
  if (value === null || typeof value !== 'object') {
    return false
  }

  // Check for plain object
  const proto = Object.getPrototypeOf(value)
  return proto === null || proto === Object.prototype
}

/**
 * Deep merge two objects with circular reference detection
 */
function deepMerge<T extends Record<string, any>>(
  target: T,
  source: Partial<T>,
  seen: WeakSet<object> = new WeakSet()
): T {
  const output = { ...target }

  // Detect circular references
  if (seen.has(source as object)) {
    return output
  }

  // Add source to seen set
  if (typeof source === 'object' && source !== null) {
    seen.add(source as object)
  }

  for (const key in source) {
    const sourceValue = source[key]

    // Skip undefined values
    if (sourceValue === undefined) {
      continue
    }

    // Handle null explicitly
    if (sourceValue === null) {
      output[key] = null as any
      continue
    }

    // Only merge plain objects (avoid DOM nodes, Dates, etc.)
    if (isPlainObject(sourceValue) && !Array.isArray(sourceValue)) {
      const targetValue = target[key]

      // If target value is also a plain object, merge recursively
      if (isPlainObject(targetValue)) {
        output[key] = deepMerge(targetValue as any, sourceValue as any, seen)
      } else {
        // Otherwise, use source value
        output[key] = sourceValue as any
      }
    } else {
      // Primitive, array, or special object - just assign
      output[key] = sourceValue as any
    }
  }

  return output
}

/**
 * Merge user theme with default theme
 * Creates a fresh default theme copy to avoid circular references
 */
export function mergeTheme(userTheme: Partial<ThemeConfig>): ThemeConfig {
  // Create a fresh copy of defaultTheme to avoid any shared references
  const freshDefault = JSON.parse(JSON.stringify(defaultTheme))
  return deepMerge(freshDefault, userTheme)
}

/**
 * Merge user translations with default translations
 * Creates a fresh default translations copy to avoid circular references
 */
export function mergeTranslations(
  userTranslations: Partial<ThemeTranslations>
): ThemeTranslations {
  // Create a fresh copy to avoid any shared references
  const freshDefault = JSON.parse(JSON.stringify(defaultTranslations))
  return deepMerge(freshDefault, userTranslations)
}

/**
 * Generate CSS variables from theme
 * Converts theme config to CSS custom properties for runtime theming
 */
export function generateCssVariables(theme: ThemeConfig): Record<string, string> {
  const vars: Record<string, string> = {}

  // Colors
  if (theme.colors) {
    Object.entries(theme.colors).forEach(([key, value]) => {
      if (value) {
        vars[`--theme-color-${kebabCase(key)}`] = value
      }
    })
  }

  // Typography
  if (theme.typography?.fontFamily) {
    vars['--theme-font-family'] = theme.typography.fontFamily
  }
  if (theme.typography?.fontFamilyHeading) {
    vars['--theme-font-family-heading'] = theme.typography.fontFamilyHeading
  }

  // Spacing
  if (theme.spacing) {
    Object.entries(theme.spacing).forEach(([key, value]) => {
      if (value) {
        vars[`--theme-spacing-${key}`] = value
      }
    })
  }

  // Border radius
  if (theme.borderRadius) {
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      if (value) {
        vars[`--theme-radius-${key}`] = value
      }
    })
  }

  // Shadows
  if (theme.shadows) {
    Object.entries(theme.shadows).forEach(([key, value]) => {
      if (value) {
        vars[`--theme-shadow-${key}`] = value
      }
    })
  }

  // Transitions
  if (theme.transitions) {
    Object.entries(theme.transitions).forEach(([key, value]) => {
      if (value) {
        vars[`--theme-transition-${key}`] = value
      }
    })
  }

  return vars
}

/**
 * Convert camelCase to kebab-case
 */
function kebabCase(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
}

/**
 * Get theme value with fallback
 */
export function getThemeValue<T>(
  theme: ThemeConfig,
  path: string,
  fallback: T
): T {
  const keys = path.split('.')
  let value: any = theme

  for (const key of keys) {
    value = value?.[key]
    if (value === undefined) {
      return fallback
    }
  }

  return value as T
}
